import React, { useEffect, useMemo, useState } from "react";
import { usersAPI } from "../../utils/usersAPI";
import { organizationsAPI } from "../../utils/organizationsAPI";
import { showToast } from "../../components/Toast";

const DEFAULT_FORM = {
  email: "",
  firstName: "",
  lastName: "",
  role: "CLIENT",
  organization: "",
  password: "",
  confirmPassword: "",
  identificationId: "",
  phone: "",
  bankAccount: "",
};

function RegisterUser() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);

  const showPasswordFields = useMemo(
    () => form.role === "ANALYST",
    [form.role],
  );

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response = await organizationsAPI.list({ page_size: 100 });
      const data = response?.data || response;
      setOrganizations(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      showToast.error(err.message || "Failed to load organizations");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      // When switching to Analyst, clear organization (analysts don't belong to an org)
      if (name === "role" && value === "ANALYST") next.organization = "";
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.role === "CLIENT" && !form.organization) {
      showToast.warning("Please select an organization");
      return;
    }

    if (showPasswordFields) {
      if (!form.password || form.password.length < 8) {
        showToast.error("Password must be at least 8 characters");
        return;
      }
      if (form.password !== form.confirmPassword) {
        showToast.error("Passwords do not match");
        return;
      }
    }

    const payload = {
      email: form.email,
      first_name: form.firstName,
      last_name: form.lastName,
      role: form.role,
      organization: form.role === "CLIENT" ? form.organization : null,
    };

    if (showPasswordFields) {
      payload.password = form.password;
      if (form.identificationId?.trim()) payload.identification_id = form.identificationId.trim();
      if (form.phone?.trim()) payload.phone = form.phone.trim();
      if (form.bankAccount?.trim()) payload.bank_account = form.bankAccount.trim();
    }

    try {
      setLoading(true);
      await usersAPI.create(payload);
      const successMsg =
        form.role === "CLIENT"
          ? "Client registered successfully. Invitation email sent."
          : "Analyst registered successfully. Credentials email sent.";
      showToast.success(successMsg);
      setForm(DEFAULT_FORM);
    } catch (err) {
      showToast.error(err.message || "Failed to register user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <div className="card" style={{ maxWidth: "640px" }}>
        <h2 className="card-title">Register User</h2>
        <p style={{ color: "var(--text-grey)", marginTop: "6px" }}>
          Create a new client or analyst account and send invitation email.
        </p>

        <form
          onSubmit={handleSubmit}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            marginTop: "24px",
          }}
        >
          <div className="form-group">
            <label>Role</label>
            <select
              name="role"
              value={form.role}
              onChange={handleChange}
              required
            >
              <option value="CLIENT">Client</option>
              <option value="ANALYST">Analyst</option>
            </select>
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Organization: show only for Client; hide entirely for Analyst */}
          {form.role === "CLIENT" && (
            <div className="form-group">
              <label>Organization</label>
              <select
                name="organization"
                value={form.organization}
                onChange={handleChange}
                required
              >
                <option value="">Select organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {form.role === "ANALYST" && (
            <p style={{ color: "var(--text-grey)", fontSize: "14px", margin: 0 }}>
              Analysts do not have an organization. Assign them to client organizations from <strong>Assign Analyst</strong> after registration.
            </p>
          )}

          {showPasswordFields && (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label>Identification ID (Rwandan)</label>
                  <input
                    type="text"
                    name="identificationId"
                    value={form.identificationId}
                    onChange={handleChange}
                    placeholder="e.g. 1xxxxxxxxxxxx"
                  />
                </div>
                <div className="form-group">
                  <label>Phone (Rwandan)</label>
                  <input
                    type="text"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="e.g. +250 788 123 456"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Bank Account (Equity Bank)</label>
                <input
                  type="text"
                  name="bankAccount"
                  value={form.bankAccount}
                  onChange={handleChange}
                  placeholder="e.g. 40011234567890"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <p style={{ color: "var(--text-grey)", marginTop: "-6px" }}>
                Analysts receive credentials immediately. Clients set their own
                password via invitation link.
              </p>
            </>
          )}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Creating..." : "Register User"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default RegisterUser;
